import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PDFViewer, PDFDownloadLink } from '@react-pdf/renderer';
import { siniestrosService }   from '../services/siniestrosService';
import { perjudicadosService } from '../services/perjudicadosService';
import { partesService }       from '../services/partesService';
import ParteTrabajosPdf        from '../pdf/ParteTrabajosPdf';

export default function SiniestroPartePdfPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [siniestro,    setSiniestro]    = useState(null);
  const [perjudicados, setPerjudicados] = useState([]);
  const [partes,       setPartes]       = useState([]);
  const [isLoading,    setIsLoading]    = useState(true);
  const [error,        setError]        = useState(null);

  useEffect(() => {
    Promise.all([
      siniestrosService.obtener(id),
      perjudicadosService.listar(id),
      partesService.listar(id),
    ])
      .then(([resSin, resPer, resPar]) => {
        setSiniestro(resSin.data);
        setPerjudicados(resPer.data);
        setPartes(resPar.data);
      })
      .catch(() => setError('Error al cargar los datos del parte'))
      .finally(() => setIsLoading(false));
  }, [id]);

  const listo = siniestro && !isLoading && !error;

  return (
    <div className="flex flex-col h-screen bg-gray-100">

      {/* ── Barra superior ── */}
      <div className="flex items-center gap-3 bg-white border-b border-gray-200 px-4 py-3 shadow-sm shrink-0">
        <button
          onClick={() => navigate(`/siniestros/${id}`)}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition text-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
          </svg>
          Volver
        </button>

        <div className="w-px h-5 bg-gray-200" />

        <span className="text-sm font-semibold text-gray-800">
          {siniestro
            ? `Parte de Trabajo — ${siniestro.nSiniestro}`
            : 'Cargando...'}
        </span>

        <div className="ml-auto">
          {listo && (
            <PDFDownloadLink
              document={
                <ParteTrabajosPdf
                  siniestro={siniestro}
                  perjudicados={perjudicados}
                  partes={partes}
                />
              }
              fileName={`parte-${siniestro.nSiniestro}.pdf`}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white
                         text-sm font-semibold px-4 py-2 rounded-xl transition shadow-sm"
            >
              {({ loading }) => (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                  </svg>
                  {loading ? 'Generando...' : 'Descargar PDF'}
                </>
              )}
            </PDFDownloadLink>
          )}
        </div>
      </div>

      {/* ── Contenido ── */}
      {isLoading && (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {error && (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {listo && (
        <PDFViewer className="flex-1 w-full border-0" showToolbar={false}>
          <ParteTrabajosPdf
            siniestro={siniestro}
            perjudicados={perjudicados}
            partes={partes}
          />
        </PDFViewer>
      )}
    </div>
  );
}
